/* eslint-disable */
// FrameDetailPanel — right info panel of the Frame Detail panel. Status lead +
// Edit/Close (pTop), config-driven spec grid + Added By cube + Notes + Linked
// Developments (the only internal scroll region) + Comments (pBody), and the
// role-gated action footer (pFoot). All field/action/role-gating bindings are
// preserved verbatim from the legacy ItemDetailModal (Discovery F1–F4).
import React from "react";
import { STAGE_THEMES } from "../../utils/devStageUtils";
import styles from "./FrameDetailModal.module.css";

// Status → badge variant (re-mapped to the REAL vocab — Discovery #98 fix).
function statusClass(s) {
  if (s === "In Development") return styles.stIn;     // amber
  if (s === "Developed") return styles.stDev;         // green
  if (s === "Ordered") return styles.stOrd;           // blue
  if (s === "Archived") return styles.stArch;         // grey
  return styles.stNeutral;
}

// Catalog-side stage pill — canonical STAGE_THEMES (Convention #50), short labels.
function stagePill(stage) {
  const t = STAGE_THEMES[stage] || STAGE_THEMES["Drawing stage"];
  let label = stage || "—";
  if (stage === "Drawing stage") label = "Drawing";
  else if (stage === "Hand made sample stage") label = "Hand made";
  else if (stage === "Color sample stage") label = "Color sample";
  else if (stage === "Done" || stage === "Completed" || stage === "Done stage" || stage === "Completed stage") label = "Done";
  else if (stage === "Cancelled") label = "Cancelled";
  else if (stage === "Paused") label = "Paused";
  return { color: t.color, bg: t.light, label };
}

const SPEC_BLOCKLIST = ["notes", "model", "brand", "sku", "collection", "status"];

export default function FrameDetailPanel({
  selected, setSelected, setEditing, setFullScreen, setFsImgIdx, setFsZoomed, setDevRequestItem,
  fields, perm, role, comments, commentsLoading, commentText, setCommentText, addComment,
  handleDelete, userNameMap, linkedDevs = [], setTab,
}) {
  const specFields = (fields || []).filter((f) => !SPEC_BLOCKLIST.includes(f.key) && selected[f.key]);
  const addedBy = selected.created_by_name || (selected.created_by && userNameMap[selected.created_by]) || selected.created_by || "—";
  const lastFull = (specFields.length + 1) % 2 === 1; // odd total → Added By spans full width
  const devCountLabel = linkedDevs.length === 0 ? "0 developments" : linkedDevs.length === 1 ? "1 development" : `${linkedDevs.length} developments`;

  const openDev = (d) => {
    if (!setTab) return;
    setTab("developments");
    setSelected(null);
    setTimeout(() => window.dispatchEvent(new CustomEvent("open_dev_panel", { detail: { id: d.id } })), 100);
  };

  return (
    <div className={styles.panel}>
      {/* pTop — status lead + Edit/Close */}
      <div className={styles.pTop}>
        {selected.status
          ? <span className={`${styles.badge} ${statusClass(selected.status)}`}>{selected.status}</span>
          : <span />}
        <div className={styles.pActions}>
          {perm.canEdit && <button type="button" className={`${styles.btn} ${styles.btnGhost}`} onClick={() => setEditing({ ...selected })}>Edit</button>}
          <button type="button" className={`${styles.btn} ${styles.btnDark}`} onClick={() => setSelected(null)}>Close</button>
        </div>
      </div>

      {/* pBody */}
      <div className={styles.pBody}>
        {/* spec grid */}
        <div className={styles.specGrid}>
          {specFields.map((f) => (
            <div key={f.key} className={styles.cube}>
              <div className={styles.cubeLabel}>{f.label}</div>
              {f.key === "assignedTo"
                ? <div style={{ marginTop: 4 }}><span className={styles.badgeAssigned}>{selected[f.key]}</span></div>
                : <div className={`${styles.cubeVal} ${f.key === "developmentOrder" ? styles.cubeMono : ""}`}>{selected[f.key]}</div>}
            </div>
          ))}
          <div className={`${styles.cube} ${lastFull ? styles.cubeFull : ""}`}>
            <div className={styles.cubeLabel}>Added By</div>
            <div className={styles.cubeVal}>{addedBy}</div>
          </div>
        </div>

        {selected.notes && <div className={styles.notesCard}>{selected.notes}</div>}

        {/* linked developments — only internal scroll */}
        <div className={styles.devSection}>
          <div className={styles.secHead}>
            <span className={styles.secTick} aria-hidden="true" />
            <span className={styles.secTitle}>Linked Developments</span>
            <span className={styles.secLine} aria-hidden="true" />
            <span className={styles.secMeta}>{devCountLabel}</span>
          </div>
          {linkedDevs.length === 0
            ? <div className={styles.devEmpty}>No linked developments yet</div>
            : (
              <div className={styles.devList}>
                {linkedDevs.map((d) => {
                  const devNum = d.dev_number || d.data?.dev_number || "—";
                  const designer = d.designer || d.data?.designer || "—";
                  const date = (d.updated_at || "").slice(0, 10) || "—";
                  const pill = stagePill(d.stage_status || d.data?.stage_status || "");
                  return (
                    <div key={d.id} className={styles.devRow} onClick={() => openDev(d)}>
                      <div className={styles.devNum}>{devNum}</div>
                      <div className={styles.devDesigner}>{designer}</div>
                      <span className={styles.stagePill} style={{ color: pill.color, background: pill.bg }}>{pill.label}</span>
                      <div className={styles.devDate}>{date}</div>
                      <div className={styles.devArrow}>↗</div>
                    </div>
                  );
                })}
              </div>
            )}
        </div>

        {/* comments */}
        <div className={styles.comments}>
          <div className={styles.secHead}>
            <span className={styles.secTick} aria-hidden="true" />
            <span className={styles.secTitle}>Comments</span>
            <span className={styles.secLine} aria-hidden="true" />
          </div>
          {commentsLoading
            ? <div className={styles.commentEmpty}>Loading…</div>
            : (
              <>
                <div className={styles.commentList}>
                  {comments.length === 0 && <div className={styles.commentEmpty}>No comments yet</div>}
                  {comments.map((c) => (
                    <div key={c.id} className={styles.commentItem}>
                      <div className={styles.commentMeta}><strong>{c.user_email?.split("@")[0]}</strong>{" · "}{new Date(c.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</div>
                      <div className={styles.commentBody}>{c.text}</div>
                    </div>
                  ))}
                </div>
                <div className={styles.commentRow}>
                  <input className={styles.commentInput} value={commentText} onChange={(e) => setCommentText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addComment()} placeholder="Add a comment…" />
                  <button type="button" className={`${styles.btn} ${styles.btnPost}`} onClick={addComment}>Post</button>
                </div>
              </>
            )}
        </div>
      </div>

      {/* pFoot — role-gated actions */}
      {(perm.canEdit || perm.canDelete) && (
        <div className={styles.pFoot}>
          <div className={styles.footRow}>
            <button type="button" className={`${styles.btn} ${styles.btnGhost} ${styles.btnFoot}`} onClick={() => { setFsImgIdx(0); setFsZoomed(false); setFullScreen(selected); setSelected(null); }}>Full Screen</button>
            {perm.canEdit && <button type="button" className={`${styles.btn} ${styles.btnGhost} ${styles.btnFoot}`} onClick={() => setEditing({ ...selected })}>Edit Frame</button>}
            {perm.canDelete && (
              <button type="button" className={styles.btnIcon} onClick={() => handleDelete(selected.id, selected.model)} aria-label="Delete">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#c0453d" strokeWidth="1.6"><path d="M3 6h18" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
              </button>
            )}
          </div>
          {role === "admin" && (
            <button type="button" className={styles.reqDev} onClick={() => { setDevRequestItem(selected); setSelected(null); }}>Request Development</button>
          )}
        </div>
      )}
    </div>
  );
}
